/* eslint-disable require-jsdoc */
import { NAME, SDP_SEMANTICS_UNIFIED_PLAN } from './constants';
import * as env from '../utils/environment';
import { CHROME_MAJOR_VERSION, IS_ANY_SAFARI, IS_IOS, IS_WECHAT } from '../utils/environment';
import { isBoolean, isObject, isUndefined, stringifyIncludeValue, throttlePromise } from '../utils/utils';
import RtcError from './rtc-error';
import { ErrorInfo, ErrorName } from './error-info';
import ErrorCode from './error-code';
import loggerManager from '../utils/log/logger-manager';
import { generateErrorMessage } from './error-generator';
import storage from '../utils/storage';
import transform from 'sdp-transform';
import kvStatManager, { KV_REPORT_KEY_DETECTOR, KV_REPORT_KEY_VIDEO_DECODE, KV_REPORT_KEY_VIDEO_ENCODE } from '../manager/kv-stat-manager';
import { FrameWorkType, VideoCodec } from '../enums';

let checkResult = {
  result: false,
  detail: {
    isBrowserSupported: false,
    isWebRTCSupported: false,
    isWebCodecsSupported: false,
    isMediaDevicesSupported: false,

    isScreenShareSupported: false,
    isSmallStreamSupported: false,

    isH264EncodeSupported: false,
    isVp8EncodeSupported: false,
    isH265EncodeSupported: false,
    isH264DecodeSupported: false,
    isVp8DecodeSupported: false,
    isH265DecodeSupported: false
  }
};

/**
 * @typedef {Object} BrowserInfo
 * @property {string} name 浏览器名
 * @property {string} version 浏览器版本号
 */
const browserInfoMap = new Map<boolean, [string, string]>([
  [env.IS_FIREFOX, ['Firefox', env.FIREFOX_VERSION]],
  [env.IS_EDG, ['Edg', env.EDG_VERSION]],
  [env.IS_CHROME, ['Chrome', env.CHROME_VERSION]],
  [env.IS_SAFARI, ['Safari', env.SAFARI_VERSION]],
  [env.IS_TBS, ['TBS', env.TBS_VERSION]],
  [env.IS_XWEB, ['XWEB', env.XWEB_VERSION]],
  [env.IS_WECHAT && env.IS_IPHONE, ['WeChat', env.WECHAT_VERSION]],
  [env.IS_WQQB, ['QQ(Win)', env.WQQB_VERSION]],
  [env.IS_MQQB, ['QQ(Mobile)', env.MQQB_VERSION]],
  [env.IS_X5MQQB, ['QQ(Mobile X5)', env.MQQB_VERSION]],
  [env.IS_MACQQB, ['QQ(Mac)', env.MACQQB_VERSION]],
  [env.IS_IPADQQB, ['QQ(iPad)', env.IPADQQB_VERSION]],
  [env.IS_MIBROWSER, ['MI', env.MI_VERSION]],
  [env.IS_HUAWEIBROWSER, ['HW', env.HUAWEI_VERSION]],
  [env.IS_SAMSUNGBROWSER, ['Samsung', env.SAMSUNG_VERSION]],
  [env.IS_OPPOBROWSER, ['OPPO', env.OPPO_VERSION]],
  [env.IS_VIVOBROWSER, ['VIVO', env.VIVO_VERSION]],
  [env.IS_EDGE, ['EDGE', env.EDGE_VERSION]],
  [env.IS_SOGOUM, ['SogouMobile', env.SOGOUM_VERSION]],
  [env.IS_SOGOU, ['Sogou', env.SOGOU_VERSION]]
]);

export function getBrowserInfo() {
  const info = browserInfoMap.get(true);
  const name = info ? info[0] : 'unknown';
  const version = info ? info[1] : 'unknown';
  return { browserName: name, browserVersion: version };
}

// 支持chrome浏览器，safari浏览器，firefox浏览器（M56+）, edge浏览器（M80+）
export const isBrowserSupported = function () {
  const MIN_FIREFOX_VERSION = 56;
  const MIN_EDG_VERSION = 80;
  if (env.IS_UCBROWSER || env.IS_EDGE) {
    return false;
  }
  if (env.IS_EDG && env.EDG_MAJOR_VERSION < MIN_EDG_VERSION) {
    return false;
  }
  if (env.IS_FIREFOX && env.FIREFOX_MAJOR_VERSION < MIN_FIREFOX_VERSION) {
    return false;
  }
  return true;
};

export const isWebCodecsSupported = function () {
  const apiList = ['VideoDecoder', 'VideoEncoder', 'AudioEncoder', 'AudioDecoder'];
  return apiList.every(api => api in window);
};

export const isMediaDevicesSupported = function () {
  if (!navigator.mediaDevices) {
    if (!isUsedInHttpProtocol()) {
      loggerManager.error(ErrorInfo.NOT_SUPPORTED_MEDIA);
    }
    return false;
  }
  const apiList = ['getUserMedia', 'enumerateDevices'];
  return apiList.filter(api => api in navigator.mediaDevices).length === apiList.length;
};

// 只输出一次 http warn
let IS_HTTP_WARN_LOGGED = false;
export function isUsedInHttpProtocol() {
  if (location.protocol === 'http:' && !env.IS_LOCAL) {
    if (!IS_HTTP_WARN_LOGGED) {
      loggerManager.error(generateErrorMessage({ key: ErrorName.NOT_SUPPORTED_HTTP }));
    }
    IS_HTTP_WARN_LOGGED = true;
    return true;
  }
  return false;
}

/**
 * 判断当前浏览器是否支持 track processor / generator
 * @returns
 */
export const isMediaStreamTrackProcessorSupported = function () {
  return (
    // @ts-ignore
    window?.OffscreenCanvas &&
    window?.MediaStreamTrackProcessor &&
    window?.MediaStreamTrackGenerator
  );
};

export const isMediaStreamTrackGeneratorSupported = function () {
  return !!window?.MediaStreamTrackGenerator;
};

export const encodeSupportStatus = async function () {
  // 如果缓存的结果中存在 false，则需要重新检测。
  // 上一次检测结果为 false， 重新检测结果为 true 的场景有：
  // 1）桌面端 Firefox 浏览器刚安装完成时不支持 H264 编码，等待 H264 编解码器加载完成后才支持 H264 编码
  // 2) 安卓端 Chrome 浏览器被首次唤醒时，创建的本地 offer 没有 H264 字段, 再次检测时有 H264 字段
  if (checkResult.detail.isH264EncodeSupported && checkResult.detail.isVp8EncodeSupported) {
    return {
      isH264EncodeSupported: checkResult.detail.isH264EncodeSupported,
      isVp8EncodeSupported: checkResult.detail.isVp8EncodeSupported,
      isH265EncodeSupported: checkResult.detail.isH265EncodeSupported
    };
  }
  let offer;
  let h264Result = false;
  let vp8Result = false;
  let h265Result = false;
  try {
    const pc = new RTCPeerConnection();
    const canvas = document.createElement(NAME.CANVAS);
    canvas.getContext('2d');
    const fakeStream = canvas.captureStream(0);
    pc.addTrack(fakeStream.getVideoTracks()[0], fakeStream);
    offer = await pc.createOffer();
    h264Result = offer.sdp?.toLowerCase().indexOf('h264') !== -1;
    vp8Result = offer.sdp?.toLowerCase().indexOf('vp8') !== -1;
    h265Result = offer.sdp?.toLowerCase().indexOf('h265') !== -1;
    pc.close();
    return {
      isH264EncodeSupported: h264Result,
      isVp8EncodeSupported: vp8Result,
      isH265EncodeSupported: h265Result
    };
  } catch (error) {
    return {
      isH264EncodeSupported: false,
      isVp8EncodeSupported: false,
      isH265EncodeSupported: false
    };
  }
};

/**
 * 检测当前环境的解码支持
 * @return {Object} result
 */
export const decodeSupportStatus = async function () {
  if (checkResult.detail.isH264DecodeSupported && checkResult.detail.isVp8DecodeSupported) {
    return {
      isH264DecodeSupported: checkResult.detail.isH264DecodeSupported,
      isVp8DecodeSupported: checkResult.detail.isVp8DecodeSupported,
      isH265DecodeSupported: checkResult.detail.isH265DecodeSupported
    };
  }
  let offer;
  let h264Result = false;
  let vp8Result = false;
  try {
    const pc = new RTCPeerConnection();
    if (!isAddTransceiverSupported()) {
      offer = await pc.createOffer({
        offerToReceiveVideo: true
      });
    } else {
      pc.addTransceiver(NAME.VIDEO, { direction: 'recvonly' });
      offer = await pc.createOffer();
    }
    if (offer.sdp!.toLowerCase().indexOf('h264') !== -1 && !(env.IS_ANDROID_WEBVIEW && CHROME_MAJOR_VERSION < 79)) {
      h264Result = true;
    }
    if (offer.sdp!.toLowerCase().indexOf('vp8') !== -1) {
      vp8Result = true;
    }
    const h265Result = offer.sdp?.toLowerCase().indexOf('h265') !== -1;
    pc.close();
    return {
      isH264DecodeSupported: h264Result,
      isVp8DecodeSupported: vp8Result,
      isH265DecodeSupported: h265Result
    };
  } catch (error) {
    return {
      isH264DecodeSupported: false,
      isVp8DecodeSupported: false,
      isH265DecodeSupported: false
    };
  }
};

async function getCodecSupportStatus() {
  const [encodeStatus, decodeStatus] = await Promise.all([
    encodeSupportStatus(),
    decodeSupportStatus()
  ]);
  return {
    encode: {
      h264: encodeStatus.isH264EncodeSupported,
      vp8: encodeStatus.isVp8EncodeSupported,
      h265: encodeStatus.isH265EncodeSupported
    },
    decode: {
      h264: decodeStatus.isH264DecodeSupported,
      vp8: decodeStatus.isVp8DecodeSupported,
      h265: decodeStatus.isH265DecodeSupported
    }
  };
}
export const checkSystemRequirementsInternal = throttlePromise(async (frameWorkType: FrameWorkType) => {
  const now = Date.now();
  const isWebRTCSupport = isWebRTCSupported();
  // check media api
  const isMediaDevicesSupport = isMediaDevicesSupported();
  // check WebCodecs Api
  const isWebCodecsSupport = isWebCodecsSupported();
  checkResult.detail.isWebRTCSupported = isWebRTCSupport;
  checkResult.detail.isMediaDevicesSupported = isMediaDevicesSupport;
  checkResult.detail.isWebCodecsSupported = isWebCodecsSupport;
  checkResult.detail.isScreenShareSupported = isScreenCaptureApiAvailable();
  checkResult.detail.isSmallStreamSupported = isSmallStreamSupported();
  if (frameWorkType === FrameWorkType.WASM) {
    Object.assign(checkResult.detail, await detectWebCodecsSupported());
    checkResult.detail.isBrowserSupported = isWebCodecsSupport;
    checkResult.result = isMediaDevicesSupport && isWebCodecsSupport;
    if (!checkResult.result) {
      loggerManager.error(`${navigator.userAgent} ${stringifyIncludeValue(checkResult.detail, false)}`);
    }
    setCheckResultToStorage(frameWorkType);
    kvStatManager.addNumber({ key: KV_REPORT_KEY_DETECTOR.CHECK_SYSTEM_REQUIREMENT_COST, value: Date.now() - now });
    return checkResult;
  }
  // 缓存结果中：编解码及其他必要能力都支持，则返回缓存结果。有可能编码不支持 h264，但支持 vp8，此时需要重新检测。
  if (checkResult.result &&
    checkResult.detail.isH264EncodeSupported && checkResult.detail.isVp8EncodeSupported && checkResult.detail.isH265EncodeSupported &&
    checkResult.detail.isH264DecodeSupported && checkResult.detail.isVp8DecodeSupported && checkResult.detail.isH265DecodeSupported
  ) {
    return checkResult;
  }
  // check blacklists of browser
  const isBrowserSupport = isBrowserSupported();

  const { encode, decode } = await getCodecSupportStatus();
  let { h264: isH264EncodeSupported, vp8: isVp8EncodeSupported } = encode;
  let { h264: isH264DecodeSupported } = decode;
  const { h265: isH265EncodeSupported } = encode;
  const { vp8: isVp8DecodeSupported, h265: isH265DecodeSupported } = decode;

  // Android Chrome 88 以下版本，安装后第一次打开，创建的本地 offer 没有 H264 字段, 再次检测时有 H264 字段
  // 此处增加一次冗余检测，来规避这个问题。
  // ref: https://tapd.woa.com/TerWebG/prong/stories/view/1020396022876681731
  if (!isH264EncodeSupported || !isVp8EncodeSupported) {
    const result = await encodeSupportStatus();
    loggerManager.warn(`detect encode again h264:${isH264EncodeSupported} vp8:${isVp8EncodeSupported} result: ${JSON.stringify(result)}`);
    isH264EncodeSupported = result.isH264EncodeSupported;
    isVp8EncodeSupported = result.isVp8EncodeSupported;
  }

  // Android Chromium 的浏览器 h264 只支持硬件编解码，但是部分设备及浏览器版本（Webview 79-）可能有兼容性问题，无法正常硬编硬解。
  // 但基础检测无法检测出来。需要使用本地推拉流来精准检测编解码支持情况。
  // https://tapd.woa.com/20396022/prong/stories/view/1020396022117812260
  // http://tapd.oa.com/TerWebG/prong/stories/view/1020396022869798873
  if (isH264EncodeSupported && isH264DecodeSupported && env.IS_ANDROID && env.IS_CHROMIUM_BASE && !env.IS_XWEB && !env.IS_TBS && !(env.IS_OPPOBROWSER && CHROME_MAJOR_VERSION === 115)) {
    const { encode, decode } = await detectH264SupportedByFakeStreaming();
    isH264EncodeSupported = encode;
    isH264DecodeSupported = decode;
  }

  checkResult.result =
    isBrowserSupport &&
    isWebRTCSupport &&
    isMediaDevicesSupport &&
    (isH264EncodeSupported || isVp8EncodeSupported) &&
    (isH264DecodeSupported || isVp8DecodeSupported);
  checkResult.detail.isBrowserSupported = isBrowserSupport;
  checkResult.detail.isWebRTCSupported = isWebRTCSupport;
  checkResult.detail.isH264EncodeSupported = isH264EncodeSupported;
  checkResult.detail.isVp8EncodeSupported = isVp8EncodeSupported;
  checkResult.detail.isH265EncodeSupported = isH265EncodeSupported;
  checkResult.detail.isH264DecodeSupported = isH264DecodeSupported;
  checkResult.detail.isVp8DecodeSupported = isVp8DecodeSupported;
  checkResult.detail.isH265DecodeSupported = isH265DecodeSupported;

  if (!checkResult.result) {
    loggerManager.error(`${navigator.userAgent} ${stringifyIncludeValue(checkResult.detail, false)}`);
  }
  setCheckResultToStorage();
  kvStatManager.addNumber({ key: KV_REPORT_KEY_DETECTOR.CHECK_SYSTEM_REQUIREMENT_COST, value: Date.now() - now });
  return checkResult;
});

export const isTRTCSupported = function () {
  return checkResult.result;
};

export const isScreenCaptureApiAvailable = function () {
  return !!(navigator.mediaDevices && navigator.mediaDevices.getDisplayMedia);
};

export const isSetSinkIdSupported = typeof HTMLMediaElement !== 'undefined' && 'setSinkId' in HTMLMediaElement.prototype;

let detectH264SupportedPromise: Promise<{ encode: boolean; decode: boolean; }> | null = null;
/**
 * 通过本地创建两个 PeerConnection 推拉流的方式
 * 根据是否编出帧及解出帧，来检测是否支持 h264 编解码。
 * 注意，该检测方法有弊端：若 encode 不支持，则 decode 也肯定解不出数据，但不代表 decode 不支持。
 * @return {Promise}
 */
export async function detectH264SupportedByFakeStreaming(timeout = 2000): Promise<{ encode: boolean, decode: boolean; }> {
  if (detectH264SupportedPromise) return detectH264SupportedPromise;
  detectH264SupportedPromise = new Promise<{ encode: boolean, decode: boolean; }>(async resolve => {
    const result = {
      encode: false,
      decode: false
    };
    let stop: () => void = () => { };
    try {
      // 1. 用 canvas 采集视频流
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d')!;
      canvas.width = 640;
      canvas.height = 480;

      const canvasIntervalId = setInterval(() => {
        ctx.fillText('test', Math.floor(Math.random() * 640), Math.floor(Math.random() * 480));
      }, 66);

      let intervalId = -1;
      let timeoutId = -1;

      stop = () => {
        clearInterval(intervalId);
        clearInterval(canvasIntervalId);
        clearTimeout(timeoutId);
        pc1.close();
        pc2.close();
        stream.getTracks().forEach(track => track.stop());
      };

      // 检测超时
      timeoutId = setTimeout(() => {
        stop();
        resolve(result);
      }, timeout);

      const stream = canvas.captureStream();

      // 2. 创建两个 PeerConnection 推拉流
      const pc1 = new RTCPeerConnection({});
      // @ts-ignore
      const pc2 = new RTCPeerConnection({ offerToReceiveAudio: true, offerToReceiveVideo: true });
      pc1.addEventListener('icecandidate', event => pc2.addIceCandidate(event.candidate!));
      pc2.addEventListener('icecandidate', event => pc1.addIceCandidate(event.candidate!));

      pc1.addTrack(stream.getVideoTracks()[0], stream);

      const pc1Offer = await pc1.createOffer();
      await pc1.setLocalDescription(pc1Offer);
      await pc2.setRemoteDescription(pc1Offer);
      const pc2Answer = await pc2.createAnswer();
      const parsedAnswer = transform.parse(pc2Answer.sdp!);
      // 通过 sdp 设置只保留 h264 描述的方式，来告知浏览器使用 h264 编解码
      const index = parsedAnswer.media[0].rtp.findIndex(item => item.codec === 'H264');
      parsedAnswer.media[0].rtp = [parsedAnswer.media[0].rtp[index]];
      parsedAnswer.media[0].fmtp = parsedAnswer.media[0].fmtp.filter(item => item.payload === parsedAnswer.media[0].rtp[0].payload);
      if (parsedAnswer.media[0].rtcpFb) {
        parsedAnswer.media[0].rtcpFb = parsedAnswer.media[0].rtcpFb.filter(item => item.payload === parsedAnswer.media[0].rtp[0].payload);
      }
      pc2Answer.sdp = transform.write(parsedAnswer);
      await pc2.setLocalDescription(pc2Answer);
      await pc1.setRemoteDescription(pc2Answer);

      intervalId = setInterval(async () => {
        if (result.encode && result.decode) {
          stop();
          resolve(result);
        }
        const sender = pc1.getSenders()[0];
        const receiver = pc2.getReceivers()[0];
        if (!sender || !receiver) return;
        const [senderStats, receiverStats] = await Promise.all([sender.getStats(), receiver.getStats()]);
        if (!result.encode) {
          senderStats.forEach(stat => {
            // Safari 使用 kind，Chrome 使用 mediaType
            const isVideoOutbound = stat.type === 'outbound-rtp' &&
              (stat.mediaType === NAME.VIDEO || stat.kind === NAME.VIDEO);
            if (isVideoOutbound && stat.bytesSent > 0) {
              result.encode = true;
            }
          });
        }
        if (!result.decode) {
          receiverStats.forEach(stat => {
            // Safari 使用 kind，Chrome 使用 mediaType
            const isVideoInbound = stat.type === 'inbound-rtp' &&
              (stat.mediaType === NAME.VIDEO || stat.kind === NAME.VIDEO);
            if (isVideoInbound && stat.bytesReceived > 0) {
              result.decode = true;
            }
          });
        }
      }, 100);
    } catch (error) {
      stop();
      loggerManager.warn('detectH264Supported failed', error);
      // 检测过程报错，则代表 PeerConnection 建立失败，此时不代表不支持 h264，默认认为支持。
      resolve({
        encode: true,
        decode: true
      });
    }
  }).then(result => {
    // encode 为 false 时，decode 肯定解不出数据，没有参考意义。设置为默认 decode 支持。
    if (!result.encode) result.decode = true;
    if (!result.encode || !result.decode) {
      loggerManager.warn(`detectH264Supported encode: ${result.encode} decode: ${result.decode} ${env.UA_DATA_STRING}`);
    }
    return result;
  });

  return detectH264SupportedPromise;
}

let detectH265SupportedPromise: Promise<{ encode: boolean; decode: boolean; }> | null = null;
async function detectH265Supported(): Promise<{ encode: boolean, decode: boolean; }> {
  if (detectH265SupportedPromise) return detectH265SupportedPromise;
  detectH265SupportedPromise = new Promise<{ encode: boolean, decode: boolean; }>(async resolve => {
    const result = {
      encode: false,
      decode: false
    };
    let stop: () => void = () => { };
    try {
      // 1. 用 canvas 采集视频流
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d')!;
      canvas.width = 320;
      canvas.height = 240;

      const canvasIntervalId = setInterval(() => {
        ctx.fillText('test', Math.floor(Math.random() * 320), Math.floor(Math.random() * 240));
      }, 66);

      let intervalId = -1;
      let timeoutId = -1;

      stop = () => {
        clearInterval(intervalId);
        clearInterval(canvasIntervalId);
        clearTimeout(timeoutId);
        pc1.close();
        pc2.close();
        stream.getTracks().forEach(track => track.stop());
      };

      // 检测超时
      timeoutId = setTimeout(() => {
        stop();
        resolve(result);
      }, 2 * 1000);

      const stream = canvas.captureStream();

      // 2. 创建两个 PeerConnection 推拉流
      const pc1 = new RTCPeerConnection({});
      // @ts-ignore
      const pc2 = new RTCPeerConnection({ offerToReceiveAudio: true, offerToReceiveVideo: true });
      pc1.addEventListener('icecandidate', event => pc2.addIceCandidate(event.candidate!));
      pc2.addEventListener('icecandidate', event => pc1.addIceCandidate(event.candidate!));

      pc1.addTrack(stream.getVideoTracks()[0], stream);

      const pc1Offer = await pc1.createOffer();
      await pc1.setLocalDescription(pc1Offer);
      await pc2.setRemoteDescription(pc1Offer);
      const pc2Answer = await pc2.createAnswer();
      const parsedAnswer = transform.parse(pc2Answer.sdp!);
      const index = parsedAnswer.media[0].rtp.findIndex(item => item.codec === 'H265');
      parsedAnswer.media[0].rtp = [parsedAnswer.media[0].rtp[index]];
      parsedAnswer.media[0].fmtp = parsedAnswer.media[0].fmtp.filter(item => item.payload === parsedAnswer.media[0].rtp[0].payload);
      if (parsedAnswer.media[0].rtcpFb) {
        parsedAnswer.media[0].rtcpFb = parsedAnswer.media[0].rtcpFb.filter(item => item.payload === parsedAnswer.media[0].rtp[0].payload);
      }
      pc2Answer.sdp = transform.write(parsedAnswer);
      await pc2.setLocalDescription(pc2Answer);
      await pc1.setRemoteDescription(pc2Answer);

      intervalId = setInterval(async () => {
        if (result.encode && result.decode) {
          stop();
          resolve(result);
        }
        const [senderStats, receiverStats] = await Promise.all([pc1.getSenders()[0].getStats(), pc2.getReceivers()[0].getStats()]);
        if (!result.encode) {
          senderStats.forEach(stat => {
            // Safari 使用 kind，Chrome 使用 mediaType
            const isVideoOutbound = stat.type === 'outbound-rtp' &&
              (stat.mediaType === NAME.VIDEO || stat.kind === NAME.VIDEO);
            if (isVideoOutbound && stat.bytesSent > 0) {
              result.encode = true;
            }
          });
        }
        if (!result.decode) {
          receiverStats.forEach(stat => {
            // Safari 使用 kind，Chrome 使用 mediaType
            const isVideoInbound = stat.type === 'inbound-rtp' &&
              (stat.mediaType === NAME.VIDEO || stat.kind === NAME.VIDEO);
            if (isVideoInbound && stat.bytesReceived > 0) {
              result.decode = true;
            }
          });
        }
      }, 100);
    } catch (error) {
      stop();
      loggerManager.warn('detectH265Supported failed', error);
      // 检测过程报错，则代表 PeerConnection 建立失败，此时不代表不支持 h265，默认认为支持。
      resolve({
        encode: true,
        decode: true
      });
    }
  }).then(result => {
    // encode 为 false 时，decode 肯定解不出数据，没有参考意义。设置为默认 decode 支持。
    if (!result.encode) result.decode = true;
    if (!result.encode || !result.decode) {
      loggerManager.warn(`detectH265Supported encode: ${result.encode} decode: ${result.decode} ${env.UA_DATA_STRING}`);
    }
    return result;
  });

  return detectH265SupportedPromise;
}

let detectWebCodecsSupportedPromise: Promise<{ isH264EncodeSupported: boolean; isH264DecodeSupported: boolean; isVp8EncodeSupported: boolean; isVp8DecodeSupported: boolean; }> | null = null;

/**
 * 通过 WebCodecs API 实际编解码来检测 H264 和 VP8 支持情况
 * 使用 canvas 采集视频流，通过实际的编解码来检测是否支持
 * @return {Promise}
 */
async function detectWebCodecsSupported(): Promise<{ isH264EncodeSupported: boolean; isH264DecodeSupported: boolean; isVp8EncodeSupported: boolean; isVp8DecodeSupported: boolean; }> {
  if (detectWebCodecsSupportedPromise) return detectWebCodecsSupportedPromise;

  detectWebCodecsSupportedPromise = new Promise(async resolve => {
    const result = {
      isH264EncodeSupported: false,
      isH264DecodeSupported: false,
      isVp8EncodeSupported: false,
      isVp8DecodeSupported: false
    };

    // 检查 WebCodecs API 是否可用
    if (!isWebCodecsSupported()) {
      resolve(result);
      return;
    }

    let canvas: HTMLCanvasElement | null = null;
    let ctx: CanvasRenderingContext2D | null = null;
    let timeoutId: number | null = null;

    const cleanup = () => {
      if (timeoutId) clearTimeout(timeoutId);
      canvas = null;
      ctx = null;
    };

    try {
      // 1. 创建 canvas 并采集视频流
      canvas = document.createElement('canvas');
      ctx = canvas.getContext('2d')!;
      canvas.width = 320;
      canvas.height = 240;

      // 绘制动态内容
      let frameCount = 0;
      const drawFrame = () => {
        if (!ctx || !canvas) return;
        ctx.fillStyle = `hsl(${frameCount % 360}, 50%, 50%)`;
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.fillStyle = 'white';
        ctx.font = '20px Arial';
        ctx.fillText(`Frame ${frameCount}`, 10, 30);
        frameCount++;
      };

      // 检测超时
      timeoutId = setTimeout(() => {
        cleanup();
        resolve(result);
      }, 5000);

      // 测试编解码器配置
      const testConfigs = [
        {
          type: 'h264' as const,
          encodeConfig: { codec: 'avc1.42E01E', avc: { format: 'annexb' as AvcBitstreamFormat }, width: 320, height: 240, bitrate: 1000000 },
          decodeConfig: { codec: 'avc1.42E01E', avc: { format: 'annexb' } }
        },
        {
          type: 'vp8' as const,
          encodeConfig: { codec: 'vp8', width: 320, height: 240, bitrate: 1000000 },
          decodeConfig: { codec: 'vp8' }
        }
      ];

      const testResults = await Promise.all(testConfigs.map(async config => {
        const testResult = {
          type: config.type,
          encodeSupported: false,
          decodeSupported: false
        };

        let encodedData: EncodedVideoChunk;
        try {
          encodedData = await new Promise(async (resolve, reject) => {
            try {
              const encoder = new VideoEncoder({
                output: chunk => {
                  // 编码成功
                  resolve(chunk);
                  testResult.encodeSupported = true;
                },
                error: reject
              });

              encoder.configure(config.encodeConfig);

              // 创建 VideoFrame 进行编码测试
              drawFrame();
              const videoFrame = new VideoFrame(canvas!, { timestamp: 0 });
              encoder.encode(videoFrame, { keyFrame: true });
              videoFrame.close();

              await encoder.flush();
              encoder.close();
            } catch (error) {
              reject(error);
            }
          });
        } catch (error) {
          loggerManager.warn(`${config.type} encoder error:`, error);
          return testResult;
        }
        try {
          // 测试解码器
          await new Promise(async (resolve, reject) => {
            try {
              const decoder = new VideoDecoder({
                output: frame => {
                  // 解码成功
                  testResult.decodeSupported = true;
                  resolve(0);
                  frame.close();
                },
                error: reject
              });

              decoder.configure(config.decodeConfig);
              decoder.decode(encodedData);
              await decoder.flush();
              decoder.close();
            } catch (error) {
              reject(error);
            }
          });
        } catch (error) {
          loggerManager.warn(`${config.type} decoder error:`, error);
        }
        return testResult;
      }));
      // 汇总结果
      testResults.forEach(testResult => {
        if (testResult.type === 'h264') {
          result.isH264EncodeSupported = testResult.encodeSupported;
          result.isH264DecodeSupported = testResult.decodeSupported;
        } else if (testResult.type === 'vp8') {
          result.isVp8EncodeSupported = testResult.encodeSupported;
          result.isVp8DecodeSupported = testResult.decodeSupported;
        }
      });

      cleanup();
      resolve(result);
    } catch (error) {
      cleanup();
      loggerManager.warn('detectWebCodecsSupported failed:', error);
      resolve(result);
    }
  });

  return detectWebCodecsSupportedPromise;
}

export const HTTPS_API = (target: any, propertyKey: string, descriptor: PropertyDescriptor) => {
  if (location.protocol === 'http:' && !env.IS_LOCAL) {
    target[propertyKey] = () => {
      throw new RtcError({
        code: ErrorCode.INVALID_OPERATION,
        message: ErrorInfo.NOT_SUPPORTED_HTTP
      });
    };
  }
};

/**
 * 判断是否是 selected candidate pair
 * ref:
 * https://developer.mozilla.org/en-US/docs/Web/API/RTCStatsIceCandidatePairState
 * https://stackoverflow.com/questions/27230542/how-do-i-get-information-about-the-type-of-connection-of-a-webrtc-peerconnection
 * @param {RTCIceCandidatePairStats} stat
 */
export const isSelectedCandidatePair = function (stat: RTCIceCandidatePairStats) {
  if (
    stat.type === 'candidate-pair' &&
    stat.nominated &&
    // 在整个通话过程中，WebRTC 会不断检查 candidate 的有效性，已经 'succeeded' 的 candidate 可能会处于 'in-progress'
    (stat.state === 'in-progress' || stat.state === 'succeeded')
  ) {
    // Firefox 中有个不标准的字段， selected，标识 candidate 是否被选中
    // @ts-ignore
    if (isBoolean(stat.selected) && !stat.selected) {
      return false;
    }
    return true;
  }
  return false;
};

export function getDisplayResolution() {
  let displayResolution = '';
  if (screen.width) {
    const width = screen.width ? screen.width * window.devicePixelRatio : '';
    const height = screen.height ? screen.height * window.devicePixelRatio : '';
    displayResolution += `${width} * ${height}`;
  }
  return displayResolution;
}

export function isGetUserMediaSupported() {
  // @ts-ignore 兼容性
  return navigator.getUserMedia || (navigator.mediaDevices && navigator.mediaDevices.getUserMedia);
}

export function isWebAudioSupported() {
  const webAudio = {
    isSupported: false
  };
  const webAudioItems = ['AudioContext', 'webkitAudioContext', 'mozAudioContext', 'msAudioContext'];
  for (let i = 0; i < webAudioItems.length; i++) {
    if (webAudioItems[i] in window) {
      webAudio.isSupported = true;
      break;
    }
  }
  return webAudio.isSupported;
}

export function isCanvasCaptureStreamAPISupported() {
  return 'captureStream' in HTMLCanvasElement.prototype;
}

export function isCanvasSmallStreamSupported() {
  // 微信和 iOS 环境下 display:none 的 canvas 元素的渲染会出现问题
  if (IS_WECHAT || IS_IOS || (CHROME_MAJOR_VERSION && CHROME_MAJOR_VERSION < 63)) {
    return false;
  }
  return !!(isBrowserSupported() && isCanvasCaptureStreamAPISupported());
}

export function isScaleResolutionDownBySupported() {
  if (CHROME_MAJOR_VERSION < 74 || env.IS_EDGE || env.IOS_MAIN_VERSION < 11 || env.FIREFOX_MAJOR_VERSION < 46) {
    return false;
  }
  return true;
}

export function isSmallStreamSupported() {
  return isCanvasSmallStreamSupported() || isScaleResolutionDownBySupported();
}


export const isUnifiedPlanDefault = function () {
  // old browser doesn't support this interface and it's in plan-b
  if (isUndefined(window.RTCRtpTransceiver) || !isAddTransceiverSupported()) {
    return false;
  }
  // Safari supports addTransceiver() but not Unified Plan when
  // currentDirection is not defined.
  // eslint-disable-next-line no-undef
  if (!('currentDirection' in RTCRtpTransceiver.prototype)) return false;

  let peerConnection = null;
  let canAddTransceiver = false;

  try {
    // If Unified Plan is supported, addTransceiver() should not throw.
    // Chrome 70 不指定 sdpSemantics 会默认使用 plan-b，导致判断不支持 unified-plan
    // @ts-ignore
    peerConnection = new RTCPeerConnection({ sdpSemantics: SDP_SEMANTICS_UNIFIED_PLAN });
    peerConnection.addTransceiver(NAME.AUDIO);
    canAddTransceiver = true;
  } catch (error) {
    // ignore
  }
  peerConnection?.close();
  return canAddTransceiver;
};

export function isGetReceiversSupported() {
  return 'RTCPeerConnection' in window && 'getReceivers' in window.RTCPeerConnection.prototype;
}

export function isGetSendersSupported() {
  return 'RTCPeerConnection' in window && 'getSenders' in window.RTCPeerConnection.prototype;
}

export function isGetTransceiversSupported() {
  return 'RTCPeerConnection' in window && 'getTransceivers' in window.RTCPeerConnection.prototype;
}

export function isAddTransceiverSupported() {
  // iOS 11 使用 addTransceiver API 无法推流，返回 false，推流时使用 addTrack fallback。
  // iOS Chrome 访问桌面网站时，UA 判断出的 iOS 版本是 11，实际上不是。这里过滤掉 iOS Chrome 桌面版的情况。
  if (env.IOS_MAIN_VERSION === 11 && !env.IS_DESKTOP_IOS_CHROME) return false;
  return 'RTCPeerConnection' in window && 'addTransceiver' in window.RTCPeerConnection.prototype;
}

export const IS_SPC_SUPPORTED = (() => {
  if (!isAddTransceiverSupported()) return false;
  // Chrome 86 以下不使用单 PC，使用多 PC。 ref: https://tapd.woa.com/20396022/prong/stories/view/1020396022887925645
  if (env.IS_CHROMIUM_BASE && CHROME_MAJOR_VERSION < 86) return false;
  return true;
})();

export function isStopTransceiverSupported() {
  return 'RTCRtpTransceiver' in window && 'stop' in window.RTCRtpTransceiver.prototype;
}

export const IS_RTC_RTP_SENDER_SUPPORTED = 'RTCRtpSender' in window;
export function isReplaceTrackSupported() {
  return IS_RTC_RTP_SENDER_SUPPORTED && 'replaceTrack' in window.RTCRtpSender.prototype;
}

export function isSetParametersSupported() {
  return (
    IS_RTC_RTP_SENDER_SUPPORTED &&
    'setParameters' in window.RTCRtpSender.prototype &&
    isGetSendersSupported()
  );
}

export const IS_GET_SYNCHRONIZATION_SOURCES_SUPPORTED = 'RTCRtpReceiver' in window && 'getSynchronizationSources' in window.RTCRtpReceiver.prototype;
export const IS_GET_SETTINGS_SUPPORTED = window.MediaStreamTrack && 'getSettings' in MediaStreamTrack.prototype;
export const IS_GET_CAPABILITIES_SUPPORTED = window.MediaStreamTrack && 'getCapabilities' in MediaStreamTrack.prototype;
export const IS_GET_CAPABILITIES_FROM_INPUTDEVICE_SUPPORTED = typeof InputDeviceInfo !== 'undefined' && 'getCapabilities' in InputDeviceInfo.prototype;


// 经测试：Chrome 85 也有 createEncodedStreams API，但是返回的数据不合规范。
// https://github.com/w3c/webrtc-encoded-transform/issues/40
export const IS_INSERTABLE_STREAM_SUPPORTED = IS_RTC_RTP_SENDER_SUPPORTED && 'createEncodedStreams' in window.RTCRtpSender.prototype && env.getChromeMajorVersion() >= 86;
export const IS_SCRIPT_TRANSFORM_SUPPORTED = ('RTCRtpScriptTransform' in window);
export const IS_SEI_SUPPORTED = IS_RTC_RTP_SENDER_SUPPORTED && (IS_INSERTABLE_STREAM_SUPPORTED || IS_SCRIPT_TRANSFORM_SUPPORTED);


export const isWebRTCSupported = function () {
  const apiList = ['RTCPeerConnection', 'webkitRTCPeerConnection', 'RTCIceGatherer'];
  return apiList.filter(api => api in window).length > 0;
};

export function isWebCodecSupported() {
  const webCodec = {
    AudioDecoder: false,
    AudioEncoder: false,
    VideoDecoder: false,
    VideoEncoder: false,
    ImageDecoder: false
  };

  if (!isUndefined(window.AudioDecoder)) {
    webCodec.AudioDecoder = true;
  }
  if (!isUndefined(window.AudioEncoder)) {
    webCodec.AudioEncoder = true;
  }
  if (!isUndefined(window.VideoDecoder)) {
    webCodec.VideoDecoder = true;
  }
  if (!isUndefined(window.VideoEncoder)) {
    webCodec.VideoEncoder = true;
  }
  if (!isUndefined(window.ImageDecoder)) {
    webCodec.ImageDecoder = true;
  }

  return webCodec;
}

export function isMediaSessionSupported() {
  if ('mediaSession' in navigator) {
    if (!isUndefined(navigator.mediaSession.setActionHandler)) {
      return true;
    }
  }
  return false;
}

export function isWebTransportSupported() {
  // @ts-ignore
  if (!isUndefined(window.WebTransport)) {
    return true;
  }
  return false;
}

export function isSIMDSupported() {
  if (typeof WebAssembly === 'undefined') return false;
  return WebAssembly.validate(new Uint8Array([0, 97, 115, 109, 1, 0, 0, 0, 1, 5, 1, 96, 0, 1, 123, 3, 2, 1, 0, 10, 10, 1, 8, 0, 65, 0, 253, 15, 253, 98, 11]));
}

export function basis() {
  const basisInformation = {
    browser: `${env.browserInfo.name}/${env.browserInfo.version}`,
    os: env.getOSName(),
    displayResolution: getDisplayResolution(),
    isScreenShareSupported: isScreenCaptureApiAvailable(),
    isWebRTCSupported: isWebRTCSupported(),
    isGetUserMediaSupported: isGetUserMediaSupported(),
    isWebAudioSupported: isWebAudioSupported(),
    isWebSocketsSupported: 'WebSocket' in window && 2 === window.WebSocket.CLOSING,
    isWebCodecSupported: isWebCodecSupported(),
    isMediaSessionSupported: isMediaSessionSupported(),
    isWebTransportSupported: isWebTransportSupported()
  };

  // 小程序 webview 环境，则在 browser 字段中加 mini 标识
  if (navigator.userAgent.includes('miniProgram')) {
    basisInformation.browser = `mini/${basisInformation.browser}`;
  }
  return basisInformation;
}

const CACHE_KEY = 'checkResult';
// 缓存 checkResult 到 localStorage，若 ua 相同，则不再重新检测。降低耗时。
// 经测试(i7 MacBook Pro)，使用 localStorage 缓存后，检测耗时只需 5ms，而未使用的耗时在 120ms
function setCheckResultToStorage(frameWorkType = 30) {
  storage.setItem(CACHE_KEY + frameWorkType, { ua: navigator.userAgent, checkResult });
}

export function capabilityCheck(frameWorkType: FrameWorkType) {
  // 是否在http协议下运行, 如是即进行 warn 提示部分功能不可用
  isUsedInHttpProtocol();
  // 获取本地存储里的检查记录
  const cacheResult = storage.getItem(CACHE_KEY + frameWorkType);
  if (cacheResult && cacheResult.ua === navigator.userAgent && cacheResult.checkResult && checkStorageResultVersion(cacheResult.checkResult.detail, checkResult.detail)) {
    checkResult = cacheResult.checkResult;
  }
  checkSystemRequirementsInternal(frameWorkType);
}

// 检测缓存结果的 key 是否与当前版本匹配. 不匹配的两种情况: 1. 缓存由 v4 SDK 设置 2. 新版本 SDK 增加了检测字段
function checkStorageResultVersion(storageResultDetail: unknown, checkResultDetail: typeof checkResult.detail) {
  if (!isObject(storageResultDetail)) return false;
  return Object.keys(checkResultDetail).every(key => key in storageResultDetail);
}

export function isRequestVideoFrameCallbackSupported() {
  return 'requestVideoFrameCallback' in HTMLVideoElement.prototype;
}

export const IS_JITTER_BUFFER_TARGET_SUPPORTED = 'RTCRtpReceiver' in window && 'jitterBufferTarget' in window.RTCRtpReceiver.prototype;

function getKVVideoCodecNumber(key: VideoCodec) {
  const map = {
    h264: 1,
    h265: 2,
    vp8: 3,
    vp9: 4,
    av1: 5
  };
  return map[key];
}

let isVideoCodecCapabilitiesDetected = false;
export async function detectVideoCodecCapabilities() {
  try {
    // decodingInfo 最低支持到 IOS 13，encodingInfo 最低支持到 IOS 15.4
    if (isVideoCodecCapabilitiesDetected || !navigator?.mediaCapabilities?.encodingInfo) return;
    const osNum = env.getOSNumber();
    const browserCoreNum = env.getBrowserCoreNumber();

    // 未知 os 和 浏览器内核不检测。
    if (osNum === 0 || browserCoreNum === 0) return;
    isVideoCodecCapabilitiesDetected = true;
    const codecs: ('H264'|'VP8'|'VP9'|'AV1'|'H265')[] = ['H264', 'VP8', 'VP9', 'AV1', 'H265'];
    const [encoderResult, decoderResult] = await Promise.all([detectVideoEncoderCapabilities(codecs), detectVideoDecoderCapabilities(codecs)]);
    if (encoderResult) {
      // loggerManager.warn('videoenccap', encoderResult);
      Object.keys(encoderResult).forEach(key => {
        const codecNum = getKVVideoCodecNumber(key.toLowerCase() as VideoCodec);
        kvStatManager.addEnum({ key: KV_REPORT_KEY_VIDEO_ENCODE.ENCODE_CAPABILITIES, value: +`${codecNum}${+encoderResult[key].supported}${+encoderResult[key].powerEfficient}${osNum}${browserCoreNum}`, useUV: false });
      });
    }
    if (decoderResult) {
      // loggerManager.warn('videodeccap', decoderResult);
      Object.keys(decoderResult).forEach(key => {
        const codecNum = getKVVideoCodecNumber(key.toLowerCase() as VideoCodec);
        kvStatManager.addEnum({ key: KV_REPORT_KEY_VIDEO_DECODE.DECODE_CAPABILITIES, value: +`${codecNum}${+decoderResult[key].supported}${+decoderResult[key].powerEfficient}${osNum}${browserCoreNum}`, useUV: false });
      });
    }

    const { sender, receiver } = getH264ProfileLevelIds();
    kvStatManager.addEnum({ key: KV_REPORT_KEY_VIDEO_ENCODE.H264_PROFILE_LEVEL_ID_HIGH, value: +`${osNum}${browserCoreNum}${+sender.high}`, useUV: false });
    kvStatManager.addEnum({ key: KV_REPORT_KEY_VIDEO_ENCODE.H264_PROFILE_LEVEL_ID_MAIN, value: +`${osNum}${browserCoreNum}${+sender.main}`, useUV: false });
    kvStatManager.addEnum({ key: KV_REPORT_KEY_VIDEO_DECODE.H264_PROFILE_LEVEL_ID_HIGH, value: +`${osNum}${browserCoreNum}${+receiver.high}`, useUV: false });
    kvStatManager.addEnum({ key: KV_REPORT_KEY_VIDEO_DECODE.H264_PROFILE_LEVEL_ID_MAIN, value: +`${osNum}${browserCoreNum}${+receiver.main}`, useUV: false });
  } catch (error) {
    // TODO: 已知 firefox 检测可能会失败
    loggerManager.info('detectVideoCodecCapabilities failed', error);
  }
}

export async function detectVideoEncoderCapabilities(codecs: ('H264'|'VP8'|'VP9'|'AV1'|'H265')[], width = 1920, height = 1080, framerate = 30, bitrate = 3000) {
  const result: Record<string, MediaCapabilitiesEncodingInfo> = {};
  try {
    for (const codec of codecs) {
      // TODO: 实测 Chrome encode h264 检测硬编不准，待排查
      const item = await navigator.mediaCapabilities.encodingInfo({
        type: 'webrtc',
        video: {
          contentType: `video/${codec}`,
          width,
          height,
          bitrate,
          framerate
        }
      });
      result[codec] = item;
    }
  } catch (error) { }
  return result;
}

export async function detectVideoDecoderCapabilities(codecs: ('H264'|'VP8'|'VP9'|'AV1'|'H265')[], width = 1920, height = 1080, framerate = 30, bitrate = 3000) {
  const result: Record<string, MediaCapabilitiesDecodingInfo> = {};
  try {
    for (const codec of codecs) {
      const item = await navigator.mediaCapabilities.decodingInfo({
        type: 'webrtc',
        video: {
          contentType: `video/${codec}`,
          width,
          height,
          bitrate,
          framerate
        }
      });
      result[codec] = item;
    }
  } catch (error) {}
  return result;
}

/**
 * 获取 H264 profileLevel 支持情况
 * @returns {Object}
 */
export function getH264ProfileLevelIds() {
  const result = {
    sender: { base: false, main: false, high: false },
    receiver: { base: false, main: false, high: false }
  };

  try {
    // 检查 RTCRtpSender.getCapabilities 是否可用
    if (RTCRtpSender && typeof RTCRtpSender.getCapabilities === 'function') {
      const senderCapabilities = RTCRtpSender.getCapabilities('video');
      if (senderCapabilities && senderCapabilities.codecs) {
        // 筛选出 H264 编解码器
        const h264Codecs = senderCapabilities.codecs.filter(codec => codec.mimeType.toLowerCase() === 'video/h264');

        // 提取 profile-level-id 参数并转换为十进制
        h264Codecs.forEach(codec => {
          if (codec.sdpFmtpLine) {
            const match = codec.sdpFmtpLine.match(/profile-level-id=([0-9a-fA-F]+)/);
            if (match && match[1]) {
              const hexValue = match[1];
              const profileLevel = hexValue.slice(0, 2);
              switch (profileLevel) {
                case '42':
                  result.sender.base = true;
                  break;
                case '4d':
                  result.sender.main = true;
                  break;
                case '64':
                  result.sender.high = true;
                  break;
              }
            }
          }
        });
      }
    }

    // 检查 RTCRtpReceiver.getCapabilities 是否可用
    if (RTCRtpReceiver && typeof RTCRtpReceiver.getCapabilities === 'function') {
      const receiverCapabilities = RTCRtpReceiver.getCapabilities('video');
      if (receiverCapabilities && receiverCapabilities.codecs) {
        // 筛选出 H264 编解码器
        const h264Codecs = receiverCapabilities.codecs.filter(codec => codec.mimeType.toLowerCase() === 'video/h264');

        // 提取 profile-level-id 参数并转换为十进制
        h264Codecs.forEach(codec => {
          if (codec.sdpFmtpLine) {
            const match = codec.sdpFmtpLine.match(/profile-level-id=([0-9a-fA-F]+)/);
            if (match && match[1]) {
              const hexValue = match[1];
              const profileLevel = hexValue.slice(0, 2);
              switch (profileLevel) {
                case '42':
                  result.receiver.base = true;
                  break;
                case '4d':
                  result.receiver.main = true;
                  break;
                case '64':
                  result.receiver.high = true;
                  break;
              }
            }
          }
        });
      }
    }
  } catch (error) {
    loggerManager.warn('get H264 profile levelId failed', error);
  }

  return result;
}

// ===================== TrackGenerator Output Detection =====================

/**
 * Check if the current environment supports MediaStreamTrackGenerator + VideoFrame
 * for Worker-based direct video track output (bypassing main thread canvas).
 * Requires:
 *  - MediaStreamTrackGenerator (Chrome 94+)
 *  - VideoFrame (Chrome 94+)
 */
let _trackGeneratorOutputSupported: boolean | null = null;

export function isTrackGeneratorOutputSupported(): boolean {
  if (_trackGeneratorOutputSupported !== null) return _trackGeneratorOutputSupported;

  const hasTrackGenerator = typeof MediaStreamTrackGenerator !== 'undefined';
  const hasVideoFrame = typeof VideoFrame !== 'undefined';

  _trackGeneratorOutputSupported = hasTrackGenerator && hasVideoFrame;

  loggerManager.debug(`TrackGenerator support: ${_trackGeneratorOutputSupported} (MediaStreamTrackGenerator=${hasTrackGenerator}, VideoFrame=${hasVideoFrame})`);

  return _trackGeneratorOutputSupported;
}

// ===================== OffscreenCanvas Worker Rendering Detection =====================

let _offscreenCanvasWorkerSupported: boolean | null = null;

/**
 * Unified detection: check if the current environment supports the full Worker rendering pipeline.
 * This includes both rendering and frame extraction capabilities to ensure the entire pipeline
 * (frame extraction → rendering → track production) runs in the Worker thread.
 *
 * Requirements:
 *  - OffscreenCanvas with 2d context
 *  - MediaStreamTrackGenerator + VideoFrame (TrackGenerator direct output, required for Worker mode)
 *  - At least one of VideoFrame or createImageBitmap (for frame transfer)
 *  - MediaStreamTrackProcessor (for Worker-side frame extraction, Chrome 94+)
 *  - ReadableStream transferable via postMessage (Chrome 87+)
 *  - NOT Safari/iOS (known compatibility issues)
 *
 * Note: bitmaprenderer fallback has been removed. Worker mode now requires TrackGenerator
 * for direct video track output. If TrackGenerator is not available, the system falls back
 * to main thread 2d rendering mode entirely.
 *
 * Worker rendering mode requires the full pipeline to avoid main-thread blocking:
 * if frame extraction cannot run in the Worker (missing MediaStreamTrackProcessor or
 * non-transferable ReadableStream), the entire Worker mode is disabled.
 */
export function isOffscreenCanvasWorkerSupported(): boolean {
  if (_offscreenCanvasWorkerSupported !== null) return _offscreenCanvasWorkerSupported;

  // Safari and iOS browsers have known issues with OffscreenCanvas and related APIs
  if (IS_ANY_SAFARI) {
    _offscreenCanvasWorkerSupported = false;
    return false;
  }

  // Check OffscreenCanvas 2d context support
  let offscreenCanvas2dSupported = false;
  try {
    if (typeof OffscreenCanvas !== 'undefined') {
      const canvas = new OffscreenCanvas(1, 1);
      offscreenCanvas2dSupported = !!canvas.getContext('2d');
    }
  } catch {
    // OffscreenCanvas 2d not supported
  }

  _offscreenCanvasWorkerSupported =
    offscreenCanvas2dSupported &&
    isTrackGeneratorOutputSupported() &&
    (typeof VideoFrame !== 'undefined' || typeof createImageBitmap === 'function') &&
    // Worker frame extraction requirements: MediaStreamTrackProcessor + transferable ReadableStream
    // These ensure frame extraction runs entirely in the Worker, not on the main thread.
    isMediaStreamTrackProcessorSupported() &&
    isReadableStreamTransferable();

  return _offscreenCanvasWorkerSupported;
}

/**
 * Check if ReadableStream can be transferred via postMessage.
 * This is required for sending MediaStreamTrackProcessor's ReadableStream to a Worker.
 * Uses a lightweight detection: attempts to construct a ReadableStream and checks
 * whether it appears in the transferable types.
 */
function isReadableStreamTransferable(): boolean {
  try {
    // ReadableStream must exist
    if (typeof ReadableStream === 'undefined') return false;

    // Quick detection: try to create a ReadableStream and test transferability
    // by checking if structuredClone with transfer list works.
    // Note: In Chrome 87+, ReadableStream is transferable.
    const stream = new ReadableStream();
    // Use MessageChannel to test transferability without actually needing a Worker
    const { port1, port2 } = new MessageChannel();
    port1.postMessage(stream, [stream as unknown as Transferable]);
    port1.close();
    port2.close();
    return true;
  } catch {
    return false;
  }
}
